/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Stack, Box, Button, HStack, Text} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import LoadingSpinner from '../loading-spinner'
import {EditIcon, BinIcon} from '../icons'

/**
 * Renders a card-style box with optional edit and remove buttons. Used for
 * lists of addresses, payment methods, or any other list-type content.
 * The provided `onRemove` callback triggers a loading spinner internally
 * if given a promise.
 */
const ActionCard = ({children, editButtonProps, onEdit, onRemove, ...props}) => {
    const [showLoading, setShowLoading] = useState(false)

    const handleRemove = async () => {
        setShowLoading(true)
        try {
            return await Promise.resolve(onRemove())
        } finally {
            setShowLoading(false)
        }
    }

    return (
        <Box
            spacing={4}
            p={4}
            position="relative"
            border="1px solid"
            borderColor="gray.100"
            borderRadius="base"
            {...props}
        >
            {showLoading && <LoadingSpinner />}
            <Stack spacing={4}>
                <Box>{children}</Box>
                <Stack direction="row" spacing={4}>
                    {onEdit && (
                        <Button onClick={onEdit} variant="link" size="sm" {...editButtonProps}>
                            <HStack spacing={2}>
                                <EditIcon boxSize={4} />
                                <Text>
                                    <FormattedMessage defaultMessage="Edit" id="global.edit" />
                                </Text>
                            </HStack>
                        </Button>
                    )}
                    {onRemove && (
                        <Button variant="link" size="sm" colorScheme="red" onClick={handleRemove}>
                            <HStack spacing={2}>
                                <BinIcon boxSize={4} />
                                <Text>
                                    <FormattedMessage
                                        defaultMessage="Remove"
                                        id="action_card.action.remove"
                                    />
                                </Text>
                            </HStack>
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

ActionCard.propTypes = {
    /**
     * Props to pass to the edit button
     */
    editButtonProps: PropTypes.object,

    /** Callback fired on edit */
    onEdit: PropTypes.func,

    /** Callback fired on remove click (if promise, will toggle loading spinner) */
    onRemove: PropTypes.func,

    /** Content rendered in card */
    children: PropTypes.node
}

export default ActionCard
